import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { api } from "./api";
import { WorkOrderDto, ListWorkOrderDto, WorkOrderStatus, MutableWorkOrderDto, SendWorkOrderEmailDto } from "../models/workorder.models";
import { AzureADRoles } from "../Auth/auth-roles";

export const useGetWorkOrdersQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>('/workorders');
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60000,
		enabled: hasAccess
	});
}

export const useGetWorkOrderByIdQuery = (workOrderId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();
	const queryClient = useQueryClient();

	if (workOrderId !== null && workOrderId !== "" && workOrderId !== "new" && workOrderId !== "new-request") {
		const existingWorkOrders = queryClient.getQueryData<ListWorkOrderDto[]>(['workorders', accountId]);
		const existing = existingWorkOrders?.find(wo => wo.WorkOrderId === workOrderId);
		if (existing && !existing.Read) {
			queryClient.setQueryData<ListWorkOrderDto[]>(['workorders', accountId], (workOrders) => {
				const index = workOrders?.findIndex(wo => wo.WorkOrderId === workOrderId) ?? -1;
				if (workOrders && index !== -1)
					workOrders[index] = { ...workOrders[index], Read: true };
				return workOrders;
			});
		}
	}

	return useQuery({
		queryKey: ['workorder', accountId, workOrderId],
		queryFn: async () => {
			let res = await api.get<WorkOrderDto>(`/workorders/${workOrderId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 60000,
		enabled: hasAccess && workOrderId !== "" && workOrderId !== "new" && workOrderId !== "new-request" && workOrderId !== null
	});
}

export const useGetWorkOrdersByCategoryIdQuery = (categoryId: string | null) => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-category', accountId, categoryId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/categories/${categoryId}/workorders`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && categoryId !== null
	});
}

export const useGetWorkOrdersByLocationIdQuery = (locationId: string | null) => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-location', accountId, locationId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/locations/${locationId}/workorders`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && locationId !== null
	});
}

export const useGetWorkOrdersByAssetIdQuery = (assetId: string | null) => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-asset', accountId, assetId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/assets/${assetId}/workorders`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && assetId !== null
	});
}

export const useGetWorkOrdersByUserIdQuery = (userId: string | null) => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-user', accountId, userId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/users/${userId}/workorders`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && userId !== null
	});
}

export const useGetWorkOrdersByTeamIdQuery = (teamId: string | null) => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-team', accountId, teamId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/teams/${teamId}/workorders`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && teamId !== null
	});
}

export const useGetWorkOrdersByVendorIdQuery = (vendorId: string | null) => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-vendor', accountId, vendorId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/vendors/${vendorId}/workorders`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && vendorId !== null
	});
}

export const useGetWorkOrdersAssignedToMeQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-assigned-to-me', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/workorders/assigned-to-me`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && accountId !== null
	});
}

export const useGetWorkOrdersAssignedToTeamQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-assigned-to-my-team', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/workorders/assigned-to-my-team`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && accountId !== null
	});
}

export const useGetWorkOrdersRequestedByMeQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-requested-by-me', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/workorders/requested-by-me`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && accountId !== null
	});
}

export const useGetWorkOrdersRequestedByTeamQuery = () => { 
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workorders-requested-by-my-team', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderDto[]>(`/workorders/requested-by-my-team`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && accountId !== null
	});
}

export const useGetWorkOrderPDFQuery = (workOrderId: string | null) => {
	const accountId = useAccountId();
	const hasAccess = useAccess(AzureADRoles.All);

	return useQuery({
		queryKey: ['workorder-pdf', accountId, workOrderId],
		queryFn: async () => {
			let res = await api.get(`/workorders/pdf/${workOrderId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && workOrderId !== "" && workOrderId !== null
	});
}

export const useUpdateWorkOrderStatusMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, { workOrderId: string; newStatus: WorkOrderStatus }>({
		mutationFn: (options) => {
			return api.put(`/workorders/${options.workOrderId}/status`, {
				newStatus: options.newStatus,
				userId: accountId
			});
		},
		onSuccess: (_data, options) => {
			// Invalidate and refetch queries related to work orders
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId, options.workOrderId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
			queryClient.invalidateQueries({ queryKey: ['user', accountId] });
			queryClient.invalidateQueries({ queryKey: ['team', accountId] });
		}
	});
}

export const useCreateWorkOrderMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderDto>, Error, { workOrder: MutableWorkOrderDto; files?: File[] }>({
		mutationFn: (options) => {
			const { workOrder, files } = options;
			if (files && files.length > 0) {
				const formData = new FormData();
				formData.append('workOrder', JSON.stringify(workOrder));

				files.forEach((file) => {
					formData.append('files', file);
				});
				return api.post(`/workorders/files`, formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			} else {
				return api.post(`/workorders`, workOrder);
			}
		},
		onSuccess: (_data, options) => {
			const workOrderId = options.workOrder.WorkOrderId;
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId, workOrderId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
			queryClient.invalidateQueries({ queryKey: ['user', accountId] });
			queryClient.invalidateQueries({ queryKey: ['team', accountId] });
		}
	});
}

export const useUpdateWorkOrderMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderDto>, Error, { workOrder: MutableWorkOrderDto; files?: File[] }>({
		mutationFn: (options) => {
			const { workOrder, files } = options;
			if (files && files.length > 0) {
				const formData = new FormData();

				formData.append('workOrder', JSON.stringify(workOrder));
				files.forEach((file) => {
					formData.append('files', file);
				});
				return api.put(`/workorders/${workOrder.WorkOrderId}/files`, formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			} else {
				return api.put(`/workorders/${workOrder.WorkOrderId}`, workOrder);
			}
		},
		onSuccess: (_data, options) => {
			const workOrderId = options.workOrder.WorkOrderId;
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId, workOrderId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
			queryClient.invalidateQueries({ queryKey: ['user', accountId] });
			queryClient.invalidateQueries({ queryKey: ['team', accountId] });
		}
	});
}

export const useDeleteWorkOrderMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, WorkOrderDto>({
		mutationFn: (workOrder) => {
			return api.delete(`/workorders/${workOrder.WorkOrderId}`);
		},
		onSuccess: (_data, workOrder) => {
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorder', accountId, workOrder.WorkOrderId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-location', accountId] });
			queryClient.invalidateQueries({ queryKey: ['workorders-category', accountId] });
			queryClient.invalidateQueries({ queryKey: ['user', accountId] });
			queryClient.invalidateQueries({ queryKey: ['team', accountId] });
		}
	});
}

export const useMarkAllWorkOrdersReadMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, { Status: WorkOrderStatus }>({
		mutationFn: (options) => {
			return api.post(`/workorders/markallread`, options);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['workorders', accountId] });
		}
	});
}

export const useSendWorkOrderEmailMutation = () => {
	return useMutation<unknown, Error, { workOrderId: string; model: SendWorkOrderEmailDto }>({
		mutationFn: (options) => {
			return api.post(`/workorders/sendemail/${options.workOrderId}`, options.model);
		}
	});
}